import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { useModals, useNavigate } from "hooks";
import { IApiServiceError, useDealActions, useDealIdSelector, useMeasureParametersSelector } from "store";
import {
  CurrencyType,
  MeasureDataDraft,
  MeasureDataSplitDetails,
  MeasureParameter,
  MonitoringDataFiles
} from "store/types";

import { Drawer } from "../Drawer/Drawer";
import { GroupsForm } from "../Groups-form/GroupsForm";
import {
  getDefaultMeasureData,
  Group,
  GroupFormDealData,
  MeasureData,
  MeasureDataGroup,
  MeasureDataRound
} from "../Groups-form/GroupsFormContext";

interface Props {
  splitDetails?: MeasureDataSplitDetails;
  handleClose: () => void;
  defaultCurrency?: CurrencyType;
  deal_id?: number;
  batch_id?: number;
}

interface InitialData {
  defaultCurrency: CurrencyType; // Only for current implementation
  initialGroups: Group[];
  initialMeasureData: MeasureData[];
  deal_id: number;
  initialRoundsByDeal: Record<number, MeasureDataRound[]>;
}

export const EditMany: React.FC<Props> = ({ splitDetails, handleClose, deal_id, batch_id, defaultCurrency }) => {
  const measureParameters = useMeasureParametersSelector();
  const queryClient = useQueryClient();
  const currentDealId = useDealIdSelector();
  const { errorModal } = useModals();

  const { createManyMeasureDataAction, saveMeasureDataDraftAction, getDealAction } = useDealActions();
  const navigate = useNavigate();

  const [dealData, setDealData] = useState<GroupFormDealData | null>(null);
  const [dealTitle, setDealTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getAndSetDealData = useCallback(
    async (dealId: number) => {
      try {
        setIsLoading(true);
        const { currency, parent, sub_assets, title } = await getDealAction(dealId);

        const currencies = currency?.data || [];

        setDealTitle(title);
        setDealData({
          deal_id: dealId,
          defaultCurrency:
            currencies.length === 1
              ? (currencies[0].value as CurrencyType)
              : (currencies.find((curr) => curr.is_default)?.value as CurrencyType),
          subDeals: sub_assets.map(({ id, title }) => ({ id, title })),
          parentId: parent?.id || null
        });
      } catch (error) {
        errorModal({ msg: (error as IApiServiceError).message });
      } finally {
        setIsLoading(false);
      }
    },
    [getDealAction]
  );

  useEffect(() => {
    if (!deal_id) return setDealData(null);

    getAndSetDealData(deal_id);
  }, [deal_id]);

  const initialValues: InitialData | null = useMemo(() => {
    if (!splitDetails) return null;

    const result: InitialData = {
      defaultCurrency: defaultCurrency!,
      initialGroups: [],
      initialMeasureData: [],
      deal_id: deal_id || 0,
      initialRoundsByDeal: {}
    };

    // Measures to decide is negative or not
    const measuresCollection = new Map<number, MeasureParameter>();
    measureParameters.forEach((measure) => measuresCollection.set(measure.id, measure));

    let measureDataKey = 1;

    Object.values(splitDetails.splits).forEach((splits) => {
      const group = splits.reduce((unitedSplits: Group, split) => {
        split.monitoring_data.forEach((item) => {
          // If the measure is negative, reverse value. Values are strings
          const isNegative = Boolean(item.measure_id && measuresCollection.get(item.measure_id)?.is_negative);

          const measureData: MeasureData = getDefaultMeasureData({
            key: measureDataKey,
            group_id: split.ui_group_id,
            value: isNegative ? (Number(item.amount) * -1).toString() : item.amount,
            investor_group: item?.investor?.group || null,
            investor_id: item.investor_id,
            investor_name: item.investor?.name,
            description: item.description,
            round_id: item.round_id,
            // round_name: item.round_name || "Undefined round name",
            date: item.date,
            accounting_date: item.accounting_date,
            effective_range_from: item.effective_range_from,
            effective_range_to: item.effective_range_to,
            currency: item.currency,
            enable_split: item.enable_split,
            split_id: item.split_id,
            split_data_details_id: split.id,
            batch_id: item.batch_id,
            id: item.id,
            ...(item.investor_files?.length && {
              files: {
                localFiles: null,
                dbFiles: item.investor_files.reduce(
                  (acc, file) => {
                    acc[crypto.randomUUID()] = file;
                    return acc;
                  },
                  {} as Record<string, MonitoringDataFiles>
                )
              }
            })
          });

          measureDataKey++;

          result.initialMeasureData.push(measureData);
          unitedSplits.measure_id = item.measure_id;
        });

        // Initialize descriptions object if it doesn't exist
        if (!unitedSplits.descriptions) {
          unitedSplits.descriptions = {};
        }

        // Add split (group-level) description for this round if it exists
        if (split.round_id && split.description) {
          unitedSplits.descriptions[split.round_id] = split.description;
        }

        // Initialize files object if it doesn't exist
        if (!unitedSplits.files) {
          unitedSplits.files = {};
        }

        // Add files for this round if they exist
        if (split.round_id && split.split_files?.length) {
          unitedSplits.files[split.round_id] = {
            localFiles: null,
            dbFiles: split.split_files.reduce(
              (acc, item) => {
                acc[crypto.randomUUID()] = item;
                return acc;
              },
              {} as Record<string, MonitoringDataFiles>
            )
          };
        }
        return {
          ...unitedSplits,
          delete_measure_data_ids: [],
          original_group_measure_data_ids: split.monitoring_data
            .filter((item) => item.id != null)
            .map((item) => item.id!),
          original_measure_id: unitedSplits.measure_id,
          ...(split.monitoring_data.length && {
            total_amount: split.monitoring_data.reduce((acc, item) => acc + Number(item.amount), 0)
          }),
          deal_id: split.deal_id,
          id: split.ui_group_id,
          files: Object.keys(unitedSplits.files).length ? unitedSplits.files : null,
          descriptions: Object.keys(unitedSplits.descriptions).length ? unitedSplits.descriptions : null,
          // if current deal (parent) is different for this split, this split from child deal -> set sub deal, where this split from
          ...(deal_id !== split.deal_id && { sub_deal_id: split.deal_id })
        };
      }, {} as Group);

      result.initialRoundsByDeal = Object.entries(splitDetails.deal_rounds).reduce(
        (acc, [deal_id, rounds]) => {
          return {
            ...acc,
            [deal_id]: rounds.map((item) => ({
              id: item.round_id,
              name: item.round_name,
              is_deleted: false,
              investors: item.investors
            }))
          };
        },
        {} as Record<number, MeasureDataRound[]>
      );

      result.initialGroups.push(group);
    });

    return result;
  }, [splitDetails, measureParameters, defaultCurrency, deal_id]);

  const handleSave = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      await createManyMeasureDataAction({ ...data, edit_type: batch_id ? "batch" : "split" });

      if (currentDealId) {
        queryClient.invalidateQueries(["round-investors", currentDealId]);
      }

      handleClose();
    },
    [batch_id]
  );

  const handleSaveAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!deal_id) return;

      await saveMeasureDataDraftAction({
        deal_id,
        data
      });

      handleClose();

      if (deal_id) {
        navigate({ route: "dealMonitoringDrafts", params: { deal_id } });
      } else {
        navigate({ route: "monitoringDrafts" });
      }
    },
    [deal_id, navigate]
  );

  return (
    <Drawer open={!!initialValues} handleClose={handleClose} title={`Edit value - ${dealTitle}`}>
      <AsyncContentMui spinnerProps={{ size: 100 }} isLoading={isLoading}>
        {initialValues && dealData && (
          <GroupsForm
            onSaveAsDraft={handleSaveAsDraft}
            {...initialValues}
            // deal_id={initialValues.deal_id}
            onSave={handleSave}
            onClose={handleClose}
            disableAddGroup={!batch_id}
            disableRemoveGroup={!batch_id}
            // disableAddMeasureData
            disableDealSelect
            disableSubDealSelect
            batch_id={batch_id}
            {...dealData}
          />
        )}
      </AsyncContentMui>
    </Drawer>
  );
};
